<template>
  <panel
    class="case-questions"
    color="blue-3"
    title="Dialogue"
    scroll-mode="content"
  >
    <template v-slot:title>
      <div v-if="isAiAvailable" class="ai-switch-container">
        <toggle-switch
          class="global-toggle md-primary ui-theme-3"
          :value="isAiEnabled"
          on-color="#7D30C9"
          :readonly="readonly"
          @toggle="value => updateAiEnabled(value)"
        />
        <div class="ai-description-container">
          <label>AI Patient Dialogue</label>
          <span>
            Enables patient AI dialogue. Retains previous questions and answers if disabled.
          </span>
        </div>
      </div>
    </template>

    <div class="tabs-container">
      <tab-dropdown
        v-if="!isAiEnabled"
        label="Patient"
        :items="patientItems"
        :selected="state.activeId === 'PATIENT'"
        @selectItem="onSelectPatientItem"
      />
      <tab-dropdown
        v-if="isAiEnabled"
        label="AI Patient"
        :items="patientAiItems"
        :selected="state.activeId === 'PATIENT'"
        @selectItem="onSelectPatientItem"
      />
      <tab-dropdown
        v-for="call in caseConf.dialogue.calls"
        :key="call.id"
        :label="call.callee.en"
        :items="calleeItems"
        show-remove
        :selected="state.activeId === call.id"
        @selectItem="itemKey => onSelectCalleeItem(call.id, itemKey)"
        @removeItem="onRemoveCallee(call)"
      />

      <md-field v-if="state.showNewCallInput" md-counter class="new-call-input">
        <md-input
          ref="newCallInput"
          type="text"
          :value="state.newCallCallee"
          :maxlength="15"
          @input="(name) => updateNewCallCallee(name)"
          @keypress.enter="submitNewCall"
        />
        <md-button class="md-icon-button md-mini" @click="submitNewCall">
          <md-icon>add</md-icon>
        </md-button>
      </md-field>
      <div>
        <md-button
          :disabled="readonly || (caseConf.dialogue.calls && caseConf.dialogue.calls.length >= 7)"
          class="md-raised md-primary add-call-option-button"
          @click="showNewCallInput()"
        >
          <md-icon>add</md-icon>Call Option
        </md-button>
        <help-tooltip v-if="(caseConf.dialogue.calls && caseConf.dialogue.calls.length >= 7)" type="warning">
          You can have up to 7 call options
        </help-tooltip>
      </div>
    </div>

    <template v-if="!isAiEnabled">
      <div v-show="state.activeId === 'PATIENT'" class="non-calls-container">
        <dialogue-editor
          v-if="state.activeTab === DialogueType.Q_AND_A"
          :key="'PATIENT_QANDA'"
          class="new-globals"
          :settings="questionSettings"
          :items="caseConf.dialogue.questions || []"
          :saved-items="questions"
          :readonly="readonly"
          :errors="errors"
          :warnings="warnings"
          @update="updateQuestions"
          @playAudio="playAnswerText"
        />
        <dialogue-editor
          v-if="state.activeTab === DialogueType.COMMANDS"
          :key="'PATIENT_COMMANDS'"
          class="new-globals"
          :settings="commandSettings"
          :items="caseConf.dialogue.commands || []"
          :readonly="readonly"
          :errors="errors"
          :warnings="warnings"
          @update="updateCommands"
          @playAudio="playAnswerText"
        />
        <dialogue-editor
          v-if="state.activeTab === DialogueType.UNSOLICITED"
          :key="'PATIENT_UNSOLICITED'"
          class="new-globals"
          :settings="unsolicitedSettings"
          :items="caseConf.dialogue.unsolicited || []"
          :readonly="readonly"
          :errors="errors"
          :warnings="warnings"
          @update="updateUnsolicited"
          @playAudio="playAnswerText"
        />
      </div>
    </template>

    <template v-if="isAiEnabled && state.activeId === 'PATIENT'">
      <div
        v-if="state.activeTab === DialogueType.AI_PATIENT_PROMPT"
        class="patient-prompt"
      >
        <html-editor
          label="Patient Prompt"
          subtitle="Patient Prompts should cover pertinent patient history, demographic information, and details on current circumstances."
          placeholder="Write or paste your patient prompt here"
          :contents="promptMarkup"
          :readonly="readonly"
          :reload="state.reloadPrompt"
          :errors="errors.filter(error => !error.metadata.componentId)"
          :prevent-emojis="true"
          @update="editPrompt"
        />
        <div class="row-below-editor">
          <div class="checkbox-and-tooltip">
            <md-checkbox
              :value="caseConf.dialogue.useCurrentDate"
              @change="updateUseCurrentDate"
            >
              AI to Use Current Date
            </md-checkbox>
            <help-tooltip type="info">
              AI will use date the scenario is run when asked in sim.
            </help-tooltip>
          </div>
          <patient-prompt-dialog
            :style="{ 'align-self': 'flex-end' }"
            :case-conf="caseConf"
            :readonly="readonly"
            :prompt-contents="promptContents"
            title="Add New: Prompt"
            button-label="Prompt Template"
            @update="replacePrompt"
          />
        </div>
      </div>
      <div
        v-if="state.activeTab !== DialogueType.AI_PATIENT_PROMPT"
        class="ai-directed-dialogue non-calls-container"
      >
        <dialogue-editor
          v-if="state.activeTab === DialogueType.AI_DIRECTED_DIALOGUE"
          key="AI_QUESTIONS"
          class="ai-questions new-globals"
          :settings="aiQuestionSettings"
          :items="caseConf.dialogue.aiQuestions || []"
          :saved-items="questions"
          :readonly="readonly"
          :errors="errors"
          :warnings="warnings"
          @update="updateAiQuestions"
          @playAudio="playAnswerText"
        />
        <dialogue-editor
          v-if="state.activeTab === DialogueType.AI_DIRECTED_DIALOGUE"
          key="AI_UNSOLICITED"
          class="ai-unsolicited new-globals"
          :settings="aiUnsolicitedSettings"
          :items="caseConf.dialogue.aiUnsolicited || []"
          :readonly="readonly"
          :errors="errors"
          :warnings="warnings"
          @update="updateAiUnsolicited"
          @playAudio="playAnswerText"
        />
      </div>
    </template>

    <div v-show="state.activeId && state.activeId !== 'PATIENT'" class="calls-container">
      <div v-for="call in caseConf.dialogue.calls" v-show="state.activeId === call.id" :key="call.id" class="call-container">
        <div class="call-header">
          <voice-selector
            :voice-key="call.calleeVoiceId"
            :voices="voices"
            :readonly="readonly"
            display-row
            :disabled-voices="[caseConf.voiceKey]"
            @update="voiceKey => updateCallVoice(call.id, voiceKey)"
          />
        </div>
        <dialogue-editor
          v-show="state.activeId === call.id && state.activeTab === DialogueType.Q_AND_A"
          id="call-questions"
          :key="`${call.id}_QANDA`"
          class="new-globals"
          :settings="callSettings"
          :items="call.items || []"
          :readonly="readonly"
          :errors="errors"
          :warnings="warnings"
          :title-prefix="call.callee.en + ': '"
          @update="(items) => updateCallItems(call.id, items)"
          @playAudio="(item) => playAnswerText(item, call.calleeVoiceId)"
        />
        <dialogue-editor
          v-show="state.activeId === call.id && state.activeTab === DialogueType.UNSOLICITED"
          id="call-unsolicited"
          :key="`${call.id}_UNSOLICITED`"
          class="new-globals"
          :settings="callUnsolicitedSettings"
          :items="call.unsolicited || []"
          :readonly="readonly"
          :errors="errors"
          :warnings="warnings"
          :title-prefix="call.callee.en + ': '"
          @update="(items) => updateCallUnsolicited(call.id, items)"
          @playAudio="(item) => playAnswerText(item, call.calleeVoiceId)"
        />
      </div>
    </div>
    <md-dialog-confirm
      :md-active.sync="state.callRemovalConfirmOpen"
      md-title="Remove call"
      :md-content="state.pendingRemovalCall ? `Are you sure you want to remove the call option to ${state.pendingRemovalCall.callee.en} ?` : ''"
      md-confirm-text="Remove"
      md-cancel-text="Cancel"
      @md-cancel="cancelPendingCallRemoval"
      @md-confirm="removePendingRemovalCall"
    />
  </panel>
</template>

<script>
import get from 'lodash/get';
import sample from 'lodash/sample';
import Chance from 'chance';

import HelpTooltip from '~lib/common/components/HelpTooltip.vue';
import HtmlEditor from '~lib/common/components/HtmlEditor/HtmlEditor.vue';

import Panel from '../common/Panel';
import DialogueEditor from './DialogueEditor.vue';
import TabDropdown from './TabDropdown.vue';
import VoiceSelector from '../common/VoiceSelector.vue';
import ToggleSwitch from '../common/ToggleSwitch.vue';
import PatientPromptDialog from '../common/PromptDialog/PatientPromptContainer';
import servicesClient from '../../providers/servicesClient';

const chance = new Chance();

const maleVoices = ['OSWALD', 'ROBERT', 'JOE', 'JAMES', 'CALVIN']
const femaleVoices = ['BETH', 'MARY', 'MAYA', 'PENNY', 'CHRISTINE'];

const DialogueType = {
  Q_AND_A: 'Q_AND_A',
  COMMANDS: 'COMMANDS',
  UNSOLICITED: 'UNSOLICITED',
  AI_PATIENT_PROMPT: 'AI_PATIENT_PROMPT',
  AI_DIRECTED_DIALOGUE: 'AI_DIRECTED_DIALOGUE',
};

export default {
  name: 'case-questions',
  components: {
    Panel,
    HelpTooltip,
    DialogueEditor,
    TabDropdown,
    HtmlEditor,
    PatientPromptDialog,
    VoiceSelector,
    ToggleSwitch,
  },
  props: {
    caseConf: {
      type: Object,
      default: () => ({
        language: 'en',
      }),
    },
    questions: {
      type: Array,
      default: () => [],
    },
    readonly: {
      type: Boolean,
      default: false,
    },
    submitting: {
      type: Boolean,
    },
    savingItemId: {
      type: String,
      default: null,
    },
    user: {
      type: Object,
      default: null,
    },
    voices: {
      type: Array,
      default: () => []
    },
    navigation: {
      type: Object,
      default: () => null,
    },
  },
  data() {
    return {
      questionSettings: {
        type: DialogueType.Q_AND_A,
        onText: 'Patient: On-at-Start Questions',
        offText: 'Patient: Off-at-Start Questions',
        questionLabel: 'Question',
        answerLabel: 'Answer',
        newQuestionLabel: 'Add a question',
        newAnswerLabel: 'Add an answer',
      },
      commandSettings: {
        type: DialogueType.COMMANDS,
        onText: 'Patient: On-at-Start Commands',
        offText: 'Patient: Off-at-Start Commands',
        onSubtitle: 'Commands need to be connected to their desired effects in Case Logic',
        questionLabel: 'Command',
        newQuestionLabel: 'Add a command',
        isAnswerDisabled: true,
      },
      unsolicitedSettings: {
        type: DialogueType.UNSOLICITED,
        offText: 'Patient: Unsolicited',
        offSubtitle: 'Unsolicited needs to be triggered by an action or timer in Case Logic',
        onDisabled: true,
        isQuestionDisabled: true,
        answerLabel: 'Patient Unsolicited',
        newAnswerLabel: 'Add a question or statement to say'
      },
      callSettings: {
        onText: 'On-at-Start Questions',
        offText: 'Off-at-Start Questions',
        questionLabel: 'Dialogue',
        answerLabel: 'Response',
        newQuestionLabel: 'Add a question',
        newAnswerLabel: 'Add an answer',
      },
      callUnsolicitedSettings: {
        type: DialogueType.UNSOLICITED,
        offText: 'Unsolicited',
        offSubtitle: 'Unsolicited needs to be triggered by an action or timer in Case Logic',
        onDisabled: true,
        isQuestionDisabled: true,
        answerLabel: 'Callee Unsolicited',
        newAnswerLabel: 'Add a question or statement to say'
      },
      aiQuestionSettings: {
        type: DialogueType.Q_AND_A,
        onText: 'AI: Listen For & Say This',
        onSubtitle: 'Have the AI listen for a specific question and respond with a specific answer',
        offText: 'AI: Say This',
        questionLabel: 'Listen For',
        answerLabel: 'Say This',
        reasonLabel: 'Reason for Response',
        newQuestionLabel: 'Add a question or statement to listen for',
        newAnswerLabel: 'Add a question or answer to be said',
        newReasonLabel: 'Add a reason for this response',
        offDisabled: true,
        isAI: true,
        fullWidth: true,
      },
      aiUnsolicitedSettings: {
        type: DialogueType.UNSOLICITED,
        offText: 'AI: Say This',
        offSubtitle: 'Have the AI make specific statements through scenario logic',
        onDisabled: true,
        isQuestionDisabled: true,
        answerLabel: 'Say This',
        reasonLabel: 'Reason for Response',
        newAnswerLabel: 'Add a question or statement to say',
        newReasonLabel: 'Add a reason for this response',
        isAI: true,
        fullWidth: true,
      },
      patientItems: [
        { key: DialogueType.Q_AND_A, label: 'Q & A' },
        { key: DialogueType.COMMANDS, label: 'Commands' },
        { key: DialogueType.UNSOLICITED, label: 'Unsolicited' },
      ],
      patientAiItems: [
        { key: DialogueType.AI_PATIENT_PROMPT, label: 'Prompt' },
        { key: DialogueType.AI_DIRECTED_DIALOGUE, label: 'Listen For/Say This' },
      ],
      calleeItems: [
        { key: DialogueType.Q_AND_A, label: 'Q & A' },
        { key: DialogueType.UNSOLICITED, label: 'Unsolicited' },
      ],
      state: {
        activeId: 'PATIENT',
        activeTab: this.caseConf.dialogue.aiEnabled ? DialogueType.AI_PATIENT_PROMPT : DialogueType.Q_AND_A,
        showNewCallInput: false,
        newCallCallee: '',
        pendingRemovalCall: null,
        callRemovalConfirmOpen: false,
        reloadPrompt: false,
      },
      DialogueType,
    };
  },
  computed: {
    errors() {
      const errors = get(
        this.$store.getters['validation/errorsByField'],
        'dialogue',
        []
      );
      return errors;
    },
    warnings() {
      const warnings = get(
        this.$store.getters['validation/visibleWarningsByField'],
        'dialogue',
        []
      );
      return warnings;
    },
    isAiAvailable() {
      return this.user['https://vrpatients.com/aiActive'];
    },
    isAiEnabled() {
      return this.caseConf.dialogue.aiEnabled;
    },
    promptMarkup() {
      return get(this.caseConf, 'dialogue.patientPrompt.markup.en');
    },
    promptContents() {
      return get(this.caseConf, 'dialogue.patientPrompt.content.en');
    },
  },
  watch: {
    navigation: {
      handler() {
        this.navigationHandler();
      },
      deep: true
    },
  },
  mounted() {
    this.navigationHandler();
  },
  methods: {
    navigationHandler() {
      if (this.navigation && this.navigation.category === 'dialogue') {
        if (this.isAiEnabled) {
          if (this.navigation.params && this.navigation.params.componentId) {
            this.showTab('PATIENT', DialogueType.AI_DIRECTED_DIALOGUE);
          } else {
            this.showTab('PATIENT', DialogueType.AI_PATIENT_PROMPT);
          }
        } else {
          if (this.navigation.params && this.navigation.params.dialogueType) {
            this.showTab('PATIENT', this.navigation.params.dialogueType);
          }
        }
      }
    },
    async playAnswerText(question, withVoiceId) {
      const text = question.options.answer[this.caseConf.language];
      const voiceId = withVoiceId || this.caseConf.voiceKey;
      const response = await servicesClient.post('generate-audio', {
        text,
        voiceId,
        // alwaysGenerate: true
      });
      const audio = new Audio(response.data.s3Location);
      audio.play();
    },
    showTab(entityId, dialogueType) {
      this.state.activeTab = dialogueType;
      this.state.activeId = entityId;
    },
    updateQuestions(items) {
      this.$emit('update', {
        dialogue: {
          questions: items,
        },
      });
    },
    updateAiQuestions(items) {
      this.$emit('update', {
        dialogue: {
          aiQuestions: items
        },
      });
    },
    updateAiUnsolicited(items) {
      this.$emit('update', {
        dialogue: {
          aiUnsolicited: items
        },
      });
    },
    updateCommands(items) {
      this.$emit('update', {
        dialogue: {
          commands: items,
        },
      });
    },
    showNewCallInput() {
      this.state.showNewCallInput = true;
      this.$nextTick(() => {
        const newCallInputRef = this.$refs.newCallInput;
        if (newCallInputRef && newCallInputRef.$el) {
          newCallInputRef.$el.focus();
        }
      })
    },
    updateNewCallCallee(name) {
      this.state.newCallCallee = name;
    },
    submitNewCall() {
      if (!this.state.newCallCallee) {
        return;
      }
      const callId = chance.guid();
      const newCall = {
        id: callId,
        callee: {
          en: this.state.newCallCallee,
        },
        calleeVoiceId: this.getRandomVoice(this.caseConf.voiceKey),
        items: [],
      };
      this.caseConf.dialogue.calls = this.caseConf.dialogue.calls || [];
      this.caseConf.dialogue.calls.push(newCall)
      this.$emit('update', {
        dialogue: {
          calls: this.caseConf.dialogue.calls,
        },
      });
      this.state.newCallCallee = '';
      this.state.showNewCallInput = false;
      this.showTab(callId, DialogueType.Q_AND_A);
    },
    updateCallVoice(callId, voiceKey) {
      this.$emit('update', {
        dialogue: {
          calls: (this.caseConf.dialogue.calls || []).map((call) =>
            call.id === callId
              ? {
                  ...call,
                  calleeVoiceId: voiceKey,
                }
              : call
          ),
        },
      });
    },
    updateCallItems(callId, items) {
      this.$emit('update', {
        dialogue: {
          calls: (this.caseConf.dialogue.calls || []).map((call) =>
            call.id === callId
              ? {
                  ...call,
                  items,
                }
              : call
          ),
        },
      });
    },
    updateCallUnsolicited(callId, unsolicited) {
      this.$emit('update', {
        dialogue: {
          calls: (this.caseConf.dialogue.calls || []).map((call) =>
            call.id === callId
              ? {
                  ...call,
                  unsolicited,
                }
              : call
          ),
        },
      });
    },
    promptCallRemove(call) {
      this.state.pendingRemovalCall = call;
      this.state.callRemovalConfirmOpen = true;
    },
    removePendingRemovalCall() {
      const { pendingRemovalCall } = this.state;
      this.$emit('update', {
        dialogue: {
          calls: (this.caseConf.dialogue.calls || []).filter(
            (c) => c.id !== pendingRemovalCall.id
          ),
        },
      });
      this.showTab('PATIENT', this.isAiEnabled ? DialogueType.AI_PATIENT_PROMPT : DialogueType.Q_AND_A);
      this.state.callRemovalConfirmOpen = false;
      this.state.pendingRemovalCall = null;
    },
    updateUnsolicited(items) {
      this.$emit('update', {
        dialogue: {
          unsolicited: items,
        }
      });
    },
    cancelPendingCallRemoval() {
      this.state.callRemovalConfirmOpen = false;
      this.state.pendingRemovalCall = null;
    },
    onSelectPatientItem(itemKey) {
      this.showTab('PATIENT', itemKey);
    },
    onSelectCalleeItem(callId, itemKey) {
      this.showTab(callId, itemKey);
    },
    onRemoveCallee(call) {
      this.promptCallRemove(call);
    },
    editPrompt(value) {
      this.$emit('update', {
        dialogue: {
          patientPrompt: {
            markup: {
              en: value.markup
            },
            content: {
              en: value.content
            }
          }
        }
      });
    },
    replacePrompt(value) {
      this.editPrompt(value);
      // A hacky way to get the HtmlEditor to reload its contents
      this.state.reloadPrompt = true;
      this.$nextTick(() => {
        this.state.reloadPrompt = false;
      });
    },
    updateAiEnabled(value) {
      if (this.state.activeId === 'PATIENT') {
        if (value) {
          this.state.activeTab = DialogueType.AI_PATIENT_PROMPT;
        } else {
          this.state.activeTab = DialogueType.Q_AND_A;
        }
      }
      if (value) {
        this.$analytics.event('Authoring Tool', 'ai_dialogue_enabled', this.user.groupsData[0].name);
      }
      this.$emit('update', {
        dialogue: {
          aiEnabled: value,
        }
      });
    },
    updateUseCurrentDate(value) {
      this.$emit('update', {
        dialogue: {
          useCurrentDate: value
        }
      });
    },
    getRandomVoice(excluding) {
      const pool = [...maleVoices, ...femaleVoices].filter(voice => voice !== excluding);
      return sample(pool);
    }
  },
};
</script>

<style scoped>
.case-questions >>> .panel-content {
  overflow-y: hidden;
}

.case-questions >>> .title-container {
  align-items: flex-start;
}

.patient-prompt {
  display: flex;
  flex-direction: column;
  height: calc(100% - 55px);
}

.html-editor {
  flex: 1;
}

.row-below-editor {
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
}

.tabs-container {
  display: flex;
  flex-direction: row;
  align-items: center;
  margin-bottom: 10px;
}

.md-tabs >>> .md-tabs-navigation .md-button {
  font-size: 16px;
}

.new-call-input {
  width: 150px;
  height: 36px;
  min-height: unset;
  margin-bottom: 0;
  margin-top: 0;
  margin-left: 24px;
  margin-right: 24px;
  padding: 4px 10px !important;
}

.new-call-input input {
  width: 100%;
}

.new-call-input .md-button {
  position: absolute;
  right: 5px;
  top: 0px;
}

.add-call-option-button {
  margin: 0;
  margin-right: 10px;
  border-radius: 16px;
}

.add-call-option-button {
  margin-left: 10px;
}

.call-header {
  display: flex;
  margin-bottom: 10px;
}

.call-header .voice-selector {
  width: 480px;
}

.non-calls-container {
  height: calc(100% - 40px);
}

.calls-container {
  height: calc(100% - 120px);
}

.call-container {
  height: 100%;
}

.gender-switch-container {
  display: flex;
  align-items: center;
  margin: 0;
}

.gender-switch.md-checked >>> .md-switch-container {
  background-color: var(--md-theme-default-primary-on-, rgba(68,138,255,0.38));
}

.gender-switch.md-checked >>> .md-switch-thumb {
  background-color: var(--md-theme-default-primary, #448aff);
}

.gender-switch >>> .md-switch-container {
  background-color: var(--md-theme-default-light-accent-on-,rgba(255,82,82,.38));
}

.gender-switch >>> .md-switch-thumb {
  background-color: var(--md-theme-default-light-accent,#ff5252);
}

.md-switch-label.left {
  padding-left: 0;
  padding-right: 16px;
}

.background-blue {
  background-color: #3f83c5;
}

.tab-dropdown {
  margin-right: 8px;
}

.md-title {
  margin-bottom: 8px;
}

.ai-switch-container {
  margin-left: 32px;
  display: flex;
  align-items: center;
  font-size: 14px;
}

.ai-switch-container label {
  font-weight: 500;
  font-size: 20px;
}

.ai-description-container {
  display: flex;
  flex-direction: column;
}

.ai-directed-dialogue {
  display: flex;
  height: calc(100% - 40px);
}

.ai-unsolicited {
  margin-left: 15px;
}

.ai-directed-dialogue .list-container {
  flex: 1;
}

.checkbox-and-tooltip {
  display: flex;
  align-items: center;
  justify-content: flex-start;
}


.checkbox-and-tooltip .md-checkbox {
  margin-right: 4px;
}

.checkbox-and-tooltip .help-tooltip {
  margin-top: 2px;
}
</style>
